import { useQueries, useQuery } from '@tanstack/react-query';
import cx from 'classnames';
import Cookies from 'js-cookie';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect, useMemo } from 'react';

import ModelsPage from '../../components/pages/v2/vehicle/models';
import useBreadcrumbs from '../../components/pages/vehicle/hooks/use-breadcrumbs';
import { useAppContext } from '../../components/providers/app-context';
import {
  ADVANCED_VIEW_ACTIVE,
  DETAILED_PRICES,
  SHIPPING_LOCATION,
} from '../../static/cookies';
import forceShippingUKForCoUK from '../../utils/force-shipping-uk-for-co-uk';
import getCategoryNames from '../../utils/get-category-names';
import { isGlobalUser } from '../../utils/is-global-user';
import { isUKuser } from '../../utils/is-uk-user';
import { isUSuser } from '../../utils/is-us-user';
import getGroups from '../../utils/react-query/query-options/get-groups';
import getLasEntries from '../../utils/react-query/query-options/get-last-entries';
import getLastVehicles from '../../utils/react-query/query-options/get-last-vehicles';
import getLoggedUser from '../../utils/react-query/query-options/get-logged-user';
import getModels from '../../utils/react-query/query-options/get-models';
import getStock from '../../utils/react-query/query-options/get-stocks';
import getTranslatedSlugs from '../../utils/react-query/query-options/get-translated-slugs';
import { redirectForAdvanced } from '../../utils/redirect-for-advanced';
import slugChange301 from '../../utils/slug-change-301';
import validateCategoryPartSlugs from '../../utils/validate-category-part-slugs';

const useData = () => {
  const {
    locale = 'en',
    query: { category = '', subCategory = '', brand = '' },
  } = useRouter();
  const { detailedPrice } = useAppContext();
  const { t } = useTranslation();

  const hasSelection = !!(category && subCategory);
  const selection = hasSelection
    ? {
      category: { category, subCategory },
    }
    : undefined;

  const queries = useMemo(
    () => ({
      queries: [
        getGroups({ locale }),
        getModels({ locale, brand: brand as string, top_models: true }),
        getLasEntries({
          locale,
          part: subCategory as string || '',
          vehicle: brand as string || '',
          detailed_prices: !!detailedPrice,
        }),
        getLastVehicles({
          locale,
          part: subCategory as string || '',
          vehicle: brand as string || '',
        }),
        getStock({
          locale,
          stocks: [
            {
              part: subCategory as string || '',
              vehicle: brand as string || '',
            },
            {
              vehicle: brand as string || '',
            },
          ],
        }),
      ].map(([key, fn]) => ({
        queryKey: key,
        queryFn: fn,
        keepPreviousData: true,
      })),
    }),
    [locale, brand, subCategory]
  );

  const [
    { data: groups },
    { data: models, isLoading: loadingModels },
    { data: lastEntries, isFetched: isFetchedLastEntries },
    { data: vehicles },
    { data: stock },
  ] = useQueries(queries);

  // GTM
  useEffect(() => {
    if (isFetchedLastEntries && lastEntries && lastEntries.length > 0) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          currencyCode: 'EUR',
          impressions: lastEntries?.map(
            (item: TProductListItem, index: number) => ({
              list: 'Last Parts in Stock', // List name where the impression was viewed
              id: item.sku, // SKU of the product
              name: item.part_en, // name of the product.
              brand: item.car_brand, // car brand of the part
              category: item.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
              price: item.pricing?.price_product
                ? Number(item.pricing.price_product).toFixed(2)
                : null, // price of the part without shipping, tax and currency
              position: index, // the position of the item in the list
              dimension2: item.reference, // reference of the product
              dimension3: item.car_model, // car model of the part
              dimension4: item.car_version, // car version of the part
            })
          ),
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'EUR', // Currency of the items associated with the event, in 3-letter ISO 4217 format
          items: lastEntries?.map((item: TProductListItem, index: number) => ({
            item_list_name: 'Last Parts in Stock', // List name where the impression was viewed
            item_list_id: 'last-parts-in-stock', // List name where the impression was viewed
            item_id: item.sku, // SKU of the product
            item_name: item.part_en, // name of the product.
            item_brand: item.car_brand, // car brand of the part
            item_category: item.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
            item_category2: item.part_en, // part name === product name
            price: item.pricing?.price_product
              ? Number(item.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            index: index, // the position of the item in the list
            quantity: '1', // Hardcoded to 1
          })),
        },
      });
    }
  }, [lastEntries, isFetchedLastEntries]);

  const vehicle = useMemo(() => models?.info, [models]);

  const breadcrumbs = useBreadcrumbs({
    selection,
    groups,
    brand: vehicle?.brand,
  });

  const info = useMemo(
    () =>
      selection?.category ? getCategoryNames(selection.category, groups) : null,
    [selection, groups]
  );

  const { data: translatedSlugs } = useQuery(
    ...getTranslatedSlugs({
      lang_origin: locale,
      lang_trans: null,
      slugs: [
        {
          slug_origin: category as string,
          slug_type: 'category',
        },
        {
          slug_origin: subCategory as string,
          slug_type: 'part',
        },
      ],
    }),
    {
      enabled: !!subCategory && !!category,
      keepPreviousData: true,
    }
  );

  return {
    groups,
    selection,
    vehicle,
    info,
    breadcrumbs,
    stock,
    vehicles,
    lastEntries,
    models,
    loadingModels: loadingModels,
    translatedSlugs,
  };
};

const Models = () => {
  const data = useData();
  return (
    <main className={cx({ 'opacity-75': data.loadingModels })}>
      <ModelsPage {...data} />
    </main>
  );
};

export default Models;

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en',
  req,
  res,
  query,
  params,
  resolvedUrl,
  ...rest
}) => {
  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);
  await forceShippingUKForCoUK({ req, res, locale });

  const { category = '', subCategory = '', brand = '' } = query;
  const { default: getGenericClient } = await import('../../server/client');
  const genericClient = getGenericClient();

  try {
    const [user, groups] = await Promise.all([
      genericClient.fetchQuery(...getLoggedUser({ locale }, req)),
      genericClient.fetchQuery(...getGroups({ locale }, req)),
      category &&
      subCategory &&
      genericClient.prefetchQuery(
        ...getTranslatedSlugs(
          {
            lang_origin: locale,
            lang_trans: null,
            slugs: [
              {
                slug_origin: category as string,
                slug_type: 'category',
              },
              {
                slug_origin: subCategory as string,
                slug_type: 'part',
              },
            ],
          },
          req
        )
      ),
      genericClient.fetchQuery(
        ...getModels({ locale, brand: brand as string, top_models: true }, req)
      ),
      genericClient.prefetchQuery(
        ...getLasEntries(
          {
            locale,
            part: subCategory as string || '',
            vehicle: brand as string || '',
            detailed_prices: req.cookies[DETAILED_PRICES] === 'true',
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getLastVehicles(
          {
            locale,
            part: subCategory as string || '',
            vehicle: brand as string || '',
          },
          req
        )
      ),
      genericClient.prefetchQuery(
        ...getStock(
          {
            locale,
            stocks: [
              {
                part: subCategory,
                vehicle: brand,
              },
              {
                vehicle: brand,
              },
            ],
          },
          req
        )
      ),
    ]);

    await validateCategoryPartSlugs({
      locale,
      groups,
      category: category as string,
      subCategory: subCategory as string,
      req,
    });

    const { default: retrieveStateAndClear } = await import(
      '../../utils/retrieve-state-and-clear'
    );

    if (
      user?.staff?.is_pro &&
      !!parseInt(req.cookies[ADVANCED_VIEW_ACTIVE] || '0')
    ) {
      return redirectForAdvanced({
        locale,
        category: category as string,
        subCategory: subCategory as string,
        vehicle: brand as string,
      });
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        groups,
        shippingLocation: parseInt(req.cookies[SHIPPING_LOCATION], 10),
        detailPrices: req.cookies[DETAILED_PRICES] || null,
      },
    };
  } catch (e) {
    if (e.status === 301) {
      const newQuery: { [key: string]: string } = {
        ...params,
        ...(e.data.category && { category: e.data.category }),
        ...(e.data.part && { subCategory: e.data.part }),
        ...(e.data.brand && { brand: e.data.brand }),
      };

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: params?.category ? 'models-with-part' : 'models',
      });
    } else if (e.status === 404) {
      if (
        e?.data?.detail === 'part_identifier' ||
        e?.data?.detail === 'group_identifier'
      ) {
        return slugChange301({
          locale,
          query: {
            brand: params?.brand as string,
          },
          res,
          page: 'models',
          status: 307,
        });
      }

      return { notFound: true };
    }

    console.log(e);
    throw e;
  }
};
